/**
 * IEEE 754-2019 binary16, binary32 and binary64, done exactly.
 *
 * Decimal text is converted to a pattern straight from its exact rational value with
 * round-to-nearest, ties-to-even — not by going through a JavaScript double first, which
 * would round twice for the smaller formats and occasionally land one unit off. A pattern
 * is turned back into the exact decimal expansion of the number it stores (every binary
 * float is a finite decimal), so the rounding error shown is the true difference and not a
 * floating-point estimate of it.
 */

import { BIG_ONE, BIG_ZERO } from "@/lib/tool-kit/math/bigint";

const BIG_TWO = BigInt(2);
const BIG_FIVE = BigInt(5);
const BIG_TEN = BigInt(10);

// biome-ignore-start lint/suspicious/noBitwiseOperators: reading and building IEEE 754 bit fields

export type FormatId = "half" | "single" | "double";

export interface FloatFormat {
  id: FormatId;
  /** IEEE 754 name. */
  name: string;
  exponentBits: number;
  fractionBits: number;
  total: number;
  bias: number;
  /** Smallest exponent of a normal number, 1 − bias. */
  minExponent: number;
  /** Largest exponent of a finite number, equal to the bias. */
  maxExponent: number;
}

function makeFormat(
  id: FormatId,
  name: string,
  exponentBits: number,
  fractionBits: number
): FloatFormat {
  const bias = 2 ** (exponentBits - 1) - 1;
  return {
    id,
    name,
    exponentBits,
    fractionBits,
    total: 1 + exponentBits + fractionBits,
    bias,
    minExponent: 1 - bias,
    maxExponent: bias,
  };
}

export const FORMATS: Record<FormatId, FloatFormat> = {
  half: makeFormat("half", "binary16", 5, 10),
  single: makeFormat("single", "binary32", 8, 23),
  double: makeFormat("double", "binary64", 11, 52),
};

export type Kind = "zero" | "subnormal" | "normal" | "infinity" | "nan";

export interface Fields {
  sign: 0 | 1;
  /** The stored, biased exponent field. */
  exponent: bigint;
  fraction: bigint;
}

export function toPatternValue(fields: Fields, f: FloatFormat): bigint {
  return (
    (BigInt(fields.sign) << BigInt(f.total - 1)) |
    (fields.exponent << BigInt(f.fractionBits)) |
    fields.fraction
  );
}

export function fromPatternValue(pattern: bigint, f: FloatFormat): Fields {
  const fractionMask = (BIG_ONE << BigInt(f.fractionBits)) - BIG_ONE;
  const exponentMask = (BIG_ONE << BigInt(f.exponentBits)) - BIG_ONE;
  return {
    sign: ((pattern >> BigInt(f.total - 1)) & BIG_ONE) === BIG_ONE ? 1 : 0,
    exponent: (pattern >> BigInt(f.fractionBits)) & exponentMask,
    fraction: pattern & fractionMask,
  };
}

function maxExponentField(f: FloatFormat): bigint {
  return (BIG_ONE << BigInt(f.exponentBits)) - BIG_ONE;
}

export function classify(fields: Fields, f: FloatFormat): Kind {
  if (fields.exponent === maxExponentField(f)) {
    return fields.fraction === BIG_ZERO ? "infinity" : "nan";
  }
  if (fields.exponent === BIG_ZERO) {
    return fields.fraction === BIG_ZERO ? "zero" : "subnormal";
  }
  return "normal";
}

export interface NanInfo {
  /** The top fraction bit: set for a quiet NaN, clear for a signalling one. */
  quiet: boolean;
  /** The remaining fraction bits. */
  payload: bigint;
}

export function nanInfo(fields: Fields, f: FloatFormat): NanInfo {
  const top = BIG_ONE << BigInt(f.fractionBits - 1);
  return {
    quiet: (fields.fraction & top) === top,
    payload: fields.fraction & (top - BIG_ONE),
  };
}

// ---------------------------------------------------------------------------
// Exact values
// ---------------------------------------------------------------------------

export interface ExactParts {
  /** Integer significand. */
  mantissa: bigint;
  /** The value is mantissa × 2^exp2 (before the sign). */
  exp2: number;
}

/** The finite value a pattern stores, as mantissa × 2^exp2. Null for ∞ and NaN. */
export function exactParts(fields: Fields, f: FloatFormat): ExactParts | null {
  const kind = classify(fields, f);
  if (kind === "infinity" || kind === "nan") {
    return null;
  }
  if (kind === "zero") {
    return { mantissa: BIG_ZERO, exp2: 0 };
  }
  if (kind === "subnormal") {
    return { mantissa: fields.fraction, exp2: f.minExponent - f.fractionBits };
  }
  return {
    mantissa: (BIG_ONE << BigInt(f.fractionBits)) | fields.fraction,
    exp2: Number(fields.exponent) - f.bias - f.fractionBits,
  };
}

/** A decimal as digits × 10^pointExponent, with no leading zeros in `digits`. */
export interface DecimalDigits {
  digits: string;
  pointExponent: number;
}

/**
 * The exact decimal expansion of mantissa × 2^exp2. A negative power of two is a finite
 * decimal: m / 2^k = m · 5^k / 10^k.
 */
export function exactDigits(parts: ExactParts): DecimalDigits {
  if (parts.mantissa === BIG_ZERO) {
    return { digits: "0", pointExponent: 0 };
  }
  if (parts.exp2 >= 0) {
    return {
      digits: (parts.mantissa << BigInt(parts.exp2)).toString(),
      pointExponent: 0,
    };
  }
  const k = -parts.exp2;
  return {
    digits: (parts.mantissa * BIG_FIVE ** BigInt(k)).toString(),
    pointExponent: -k,
  };
}

function trimZeros(d: DecimalDigits): DecimalDigits {
  let end = d.digits.length;
  while (end > 1 && d.digits[end - 1] === "0") {
    end -= 1;
  }
  return {
    digits: d.digits.slice(0, end),
    pointExponent: d.pointExponent + (d.digits.length - end),
  };
}

/** Plain decimal text: `0.1000000000000000055511151231257827…`, with no exponent. */
export function plainDecimal(d: DecimalDigits): string {
  const t = trimZeros(d);
  if (t.digits === "0") {
    return "0";
  }
  if (t.pointExponent >= 0) {
    return t.digits + "0".repeat(t.pointExponent);
  }
  const places = -t.pointExponent;
  const padded = t.digits.padStart(places + 1, "0");
  return `${padded.slice(0, padded.length - places)}.${padded.slice(padded.length - places)}`;
}

/** JavaScript-style text: plain between 1e-6 and 1e21, scientific outside. */
export function shortText(d: DecimalDigits): string {
  const t = trimZeros(d);
  if (t.digits === "0") {
    return "0";
  }
  const sci = t.pointExponent + t.digits.length - 1;
  if (sci >= -6 && sci < 21) {
    return plainDecimal(t);
  }
  const mantissa =
    t.digits.length === 1 ? t.digits : `${t.digits[0]}.${t.digits.slice(1)}`;
  return `${mantissa}e${sci < 0 ? "−" : "+"}${Math.abs(sci)}`;
}

// ---------------------------------------------------------------------------
// Encoding from an exact rational
// ---------------------------------------------------------------------------

function bitLen(n: bigint): number {
  return n.toString(2).length;
}

function infinityFields(sign: 0 | 1, f: FloatFormat): Fields {
  return { sign, exponent: maxExponentField(f), fraction: BIG_ZERO };
}

/** Quiet NaN with an empty payload — what 0/0 and `NaN` produce. */
export function canonicalNan(f: FloatFormat): Fields {
  return {
    sign: 0,
    exponent: maxExponentField(f),
    fraction: BIG_ONE << BigInt(f.fractionBits - 1),
  };
}

function floorLog2(num: bigint, den: bigint): number {
  const e = bitLen(num) - bitLen(den);
  const below = e >= 0 ? num < den << BigInt(e) : num << BigInt(-e) < den;
  return below ? e - 1 : e;
}

/** Divide num/den and round half to even. */
function divideRoundEven(num: bigint, den: bigint): bigint {
  const quotient = num / den;
  const twiceRemainder = (num % den) * BIG_TWO;
  if (
    twiceRemainder > den ||
    (twiceRemainder === den && (quotient & BIG_ONE) === BIG_ONE)
  ) {
    return quotient + BIG_ONE;
  }
  return quotient;
}

/** Round num/den (positive) into `f` with ties-to-even, overflowing to infinity. */
export function encodeRational(
  sign: 0 | 1,
  num: bigint,
  den: bigint,
  f: FloatFormat
): Fields {
  if (num === BIG_ZERO) {
    return { sign, exponent: BIG_ZERO, fraction: BIG_ZERO };
  }
  let e = floorLog2(num, den);
  const quantumExponent = Math.max(e, f.minExponent) - f.fractionBits;
  const scaledNum = quantumExponent < 0 ? num << BigInt(-quantumExponent) : num;
  const scaledDen = quantumExponent > 0 ? den << BigInt(quantumExponent) : den;
  let m = divideRoundEven(scaledNum, scaledDen);
  const hidden = BIG_ONE << BigInt(f.fractionBits);
  if (e >= f.minExponent) {
    if (m >= hidden * BIG_TWO) {
      m = hidden;
      e += 1;
    }
    if (e > f.maxExponent) {
      return infinityFields(sign, f);
    }
    return { sign, exponent: BigInt(e + f.bias), fraction: m - hidden };
  }
  if (m >= hidden) {
    return { sign, exponent: BIG_ONE, fraction: BIG_ZERO };
  }
  return { sign, exponent: BIG_ZERO, fraction: m };
}

// ---------------------------------------------------------------------------
// Parsing decimal text
// ---------------------------------------------------------------------------

const DECIMAL_TOKEN = /^([+\-−]?)(\d*)(?:\.(\d*))?(?:[eE]([+\-−]?\d+))?$/;
const INFINITY_TOKEN = /^([+\-−]?)(?:inf|infinity|∞)$/i;
const NAN_TOKEN = /^[+\-−]?nan$/i;
const SEPARATORS = /[\s_]/g;
const ALL_ZEROS = /^0+$/;
const SIGN_PREFIX = /^[+-]/;
const LEADING_ZEROS = /^0+/;
const TRAILING_ZEROS = /\.?0+$/;
const MAX_ORDER = 5000;
const MAX_INPUT_LENGTH = 4000;
const COMPACT_DIGITS = 12;

export interface Rational {
  sign: 0 | 1;
  num: bigint;
  den: bigint;
}

export type DecimalParse =
  | { ok: true; kind: "finite"; value: Rational; underflowed: boolean }
  | { ok: true; kind: "infinity"; sign: 0 | 1; overflowed: boolean }
  | { ok: true; kind: "nan" }
  | { ok: false; error: "empty" | "invalid" | "tooLong" };

function signOf(token: string): 0 | 1 {
  return token === "-" || token === "−" ? 1 : 0;
}

function finiteFromParts(
  sign: 0 | 1,
  digits: string,
  exponent: number
): DecimalParse {
  const mantissa = BigInt(digits);
  if (mantissa === BIG_ZERO) {
    return {
      ok: true,
      kind: "finite",
      value: { sign, num: BIG_ZERO, den: BIG_ONE },
      underflowed: false,
    };
  }
  const trimmed = digits.replace(LEADING_ZEROS, "");
  const order = exponent + trimmed.length;
  if (order > MAX_ORDER) {
    return { ok: true, kind: "infinity", sign, overflowed: true };
  }
  if (order < -MAX_ORDER) {
    return {
      ok: true,
      kind: "finite",
      value: { sign, num: BIG_ZERO, den: BIG_ONE },
      underflowed: true,
    };
  }
  if (exponent >= 0) {
    return {
      ok: true,
      kind: "finite",
      value: {
        sign,
        num: mantissa * BIG_TEN ** BigInt(exponent),
        den: BIG_ONE,
      },
      underflowed: false,
    };
  }
  return {
    ok: true,
    kind: "finite",
    value: { sign, num: mantissa, den: BIG_TEN ** BigInt(-exponent) },
    underflowed: false,
  };
}

/** Read decimal text exactly. A lone comma is accepted as the decimal mark. */
export function parseDecimalText(text: string): DecimalParse {
  let cleaned = text.replace(SEPARATORS, "");
  if (cleaned.length === 0) {
    return { ok: false, error: "empty" };
  }
  if (cleaned.length > MAX_INPUT_LENGTH) {
    return { ok: false, error: "tooLong" };
  }
  if (!cleaned.includes(".") && cleaned.split(",").length === 2) {
    cleaned = cleaned.replace(",", ".");
  }
  const inf = INFINITY_TOKEN.exec(cleaned);
  if (inf) {
    return {
      ok: true,
      kind: "infinity",
      sign: signOf(inf[1]),
      overflowed: false,
    };
  }
  if (NAN_TOKEN.test(cleaned)) {
    return { ok: true, kind: "nan" };
  }
  const match = DECIMAL_TOKEN.exec(cleaned);
  if (!match || (match[2] === "" && (match[3] ?? "") === "")) {
    return { ok: false, error: "invalid" };
  }
  const fraction = match[3] ?? "";
  const sign = signOf(match[1]);
  const digits = `${match[2]}${fraction}`;
  if (ALL_ZEROS.test(digits)) {
    return finiteFromParts(sign, "0", 0);
  }
  const exponentText = (match[4] ?? "0").replace("−", "-");
  if (exponentText.replace(SIGN_PREFIX, "").length > 7) {
    const negative = exponentText.startsWith("-");
    return finiteFromParts(
      sign,
      "1",
      negative ? -MAX_ORDER * 2 : MAX_ORDER * 2
    );
  }
  return finiteFromParts(sign, digits, Number(exponentText) - fraction.length);
}

// ---------------------------------------------------------------------------
// Shortest round-trip text
// ---------------------------------------------------------------------------

function roundToDigits(
  d: DecimalDigits,
  n: number,
  direction: "nearest" | "down" | "up"
): DecimalDigits {
  if (d.digits.length <= n) {
    return d;
  }
  const head = d.digits.slice(0, n);
  const tail = d.digits.slice(n);
  const dropped = d.digits.length - n;
  let up = false;
  if (direction === "up") {
    up = tail.replace(/0/g, "") !== "";
  } else if (direction === "nearest") {
    const first = Number(tail[0]);
    const rest = tail.slice(1).replace(/0/g, "");
    const tie = first === 5 && rest === "";
    up = first > 5 || (first === 5 && rest !== "");
    if (tie) {
      up = Number(head.at(-1)) % 2 === 1;
    }
  }
  const headValue = up ? (BigInt(head) + BIG_ONE).toString() : head;
  return { digits: headValue, pointExponent: d.pointExponent + dropped };
}

function sameFields(a: Fields, b: Fields): boolean {
  return (
    a.sign === b.sign && a.exponent === b.exponent && a.fraction === b.fraction
  );
}

function decimalAsRational(d: DecimalDigits): { num: bigint; den: bigint } {
  const mantissa = BigInt(d.digits);
  return d.pointExponent >= 0
    ? { num: mantissa * BIG_TEN ** BigInt(d.pointExponent), den: BIG_ONE }
    : { num: mantissa, den: BIG_TEN ** BigInt(-d.pointExponent) };
}

/**
 * The shortest decimal that reads back as the same float: the fewest digits n for which
 * some n-digit decimal rounds to this pattern.
 */
export function shortestDigits(fields: Fields, f: FloatFormat): DecimalDigits {
  const parts = exactParts(fields, f);
  if (!parts || parts.mantissa === BIG_ZERO) {
    return { digits: "0", pointExponent: 0 };
  }
  const exact = trimZeros(exactDigits(parts));
  for (let n = 1; n < exact.digits.length; n++) {
    for (const direction of ["nearest", "down", "up"] as const) {
      const candidate = trimZeros(roundToDigits(exact, n, direction));
      const { num, den } = decimalAsRational(candidate);
      if (sameFields(encodeRational(fields.sign, num, den, f), fields)) {
        return candidate;
      }
    }
  }
  return exact;
}

// ---------------------------------------------------------------------------
// Rounding error
// ---------------------------------------------------------------------------

export interface Scientific {
  negative: boolean;
  /** `digits` significant digits, no point. */
  digits: string;
  /** Power of ten of the first digit: value ≈ d.ddd × 10^exponent. */
  exponent: number;
}

/** p/q to `digits` significant digits (rounded half up); null when p is 0. */
export function toScientific(
  p: bigint,
  q: bigint,
  digits: number
): Scientific | null {
  if (p === BIG_ZERO) {
    return null;
  }
  const negative = p < BIG_ZERO !== q < BIG_ZERO;
  const num = p < BIG_ZERO ? -p : p;
  const den = q < BIG_ZERO ? -q : q;
  let exponent = num.toString().length - den.toString().length;
  for (let attempt = 0; attempt < 4; attempt++) {
    const shift = digits - 1 - exponent;
    const scaledNum = shift >= 0 ? num * BIG_TEN ** BigInt(shift) : num;
    const scaledDen = shift < 0 ? den * BIG_TEN ** BigInt(-shift) : den;
    const rounded = (scaledNum * BIG_TWO + scaledDen) / (scaledDen * BIG_TWO);
    const text = rounded.toString();
    if (text.length === digits) {
      return { negative, digits: text, exponent };
    }
    exponent += text.length > digits ? 1 : -1;
  }
  return null;
}

export interface RoundingError {
  /** stored − entered, as a fraction. */
  numerator: bigint;
  denominator: bigint;
  exact: boolean;
  absolute: Scientific | null;
  relative: Scientific | null;
  /** |error| as a fraction of one unit in the last place, as a decimal text. */
  ulpFraction: string | null;
}

function parts2(parts: ExactParts): { num: bigint; den: bigint } {
  return parts.exp2 >= 0
    ? { num: parts.mantissa << BigInt(parts.exp2), den: BIG_ONE }
    : { num: parts.mantissa, den: BIG_ONE << BigInt(-parts.exp2) };
}

/** The ulp (spacing of floats) around a finite pattern, as a power-of-two rational. */
export function ulpOf(
  fields: Fields,
  f: FloatFormat
): { num: bigint; den: bigint } {
  const exponent = Math.max(Number(fields.exponent) - f.bias, f.minExponent);
  const q = exponent - f.fractionBits;
  return q >= 0
    ? { num: BIG_ONE << BigInt(q), den: BIG_ONE }
    : { num: BIG_ONE, den: BIG_ONE << BigInt(-q) };
}

export function roundingError(
  entered: Rational,
  stored: Fields,
  f: FloatFormat
): RoundingError | null {
  const parts = exactParts(stored, f);
  if (!parts) {
    return null;
  }
  const s = parts2(parts);
  const sSigned = stored.sign === 1 ? -s.num : s.num;
  const eSigned = entered.sign === 1 ? -entered.num : entered.num;
  const numerator = sSigned * entered.den - eSigned * s.den;
  const denominator = s.den * entered.den;
  if (numerator === BIG_ZERO) {
    return {
      numerator,
      denominator,
      exact: true,
      absolute: null,
      relative: null,
      ulpFraction: "0",
    };
  }
  const relNum = numerator * entered.den;
  const relDen = denominator * eSigned;
  const ulp = ulpOf(stored, f);
  const absNum = numerator < BIG_ZERO ? -numerator : numerator;
  const fraction = toScientific(absNum * ulp.den, denominator * ulp.num, 3);
  return {
    numerator,
    denominator,
    exact: false,
    absolute: toScientific(numerator, denominator, 6),
    relative: eSigned === BIG_ZERO ? null : toScientific(relNum, relDen, 6),
    ulpFraction: fraction
      ? shortText({
          digits: fraction.digits,
          pointExponent: fraction.exponent - (fraction.digits.length - 1),
        })
      : null,
  };
}

/** `5.55112e−18` style text for a Scientific value. */
export function sciText(s: Scientific): string {
  const mantissa =
    s.digits.length === 1 ? s.digits : `${s.digits[0]}.${s.digits.slice(1)}`;
  const trimmed = mantissa.includes(".")
    ? mantissa.replace(TRAILING_ZEROS, "")
    : mantissa;
  const sign = s.negative ? "−" : "";
  return `${sign}${trimmed}e${s.exponent < 0 ? "−" : "+"}${Math.abs(s.exponent)}`;
}

// ---------------------------------------------------------------------------
// Neighbours and format facts
// ---------------------------------------------------------------------------

function magnitudeMask(f: FloatFormat): bigint {
  return (BIG_ONE << BigInt(f.total - 1)) - BIG_ONE;
}

/** The next float toward +∞ (or −∞), or null for NaN and for infinity in that direction. */
export function neighbour(
  pattern: bigint,
  f: FloatFormat,
  direction: "up" | "down"
): bigint | null {
  const fields = fromPatternValue(pattern, f);
  const kind = classify(fields, f);
  if (kind === "nan") {
    return null;
  }
  const signBit = BIG_ONE << BigInt(f.total - 1);
  const magnitude = pattern & magnitudeMask(f);
  const negative = fields.sign === 1;
  const towardZero = (direction === "up") === negative;
  if (kind === "infinity" && !towardZero) {
    return null;
  }
  if (towardZero) {
    if (magnitude === BIG_ZERO) {
      return direction === "up" ? BIG_ONE : signBit | BIG_ONE;
    }
    return (negative ? signBit : BIG_ZERO) | (magnitude - BIG_ONE);
  }
  return (negative ? signBit : BIG_ZERO) | (magnitude + BIG_ONE);
}

export interface FormatFacts {
  smallestSubnormal: DecimalDigits;
  smallestNormal: DecimalDigits;
  largestFinite: DecimalDigits;
  epsilon: DecimalDigits;
}

export function formatFacts(f: FloatFormat): FormatFacts {
  const digitsOf = (pattern: bigint) => {
    const fields = fromPatternValue(pattern, f);
    const parts = exactParts(fields, f);
    const exact = parts ? trimZeros(exactDigits(parts)) : null;
    // A short exact value (65504, 0.0009765625) reads better than its round-trip form.
    return exact && exact.digits.length <= COMPACT_DIGITS
      ? exact
      : shortestDigits(fields, f);
  };
  const exponentShift = BigInt(f.fractionBits);
  const maxExponentPattern = maxExponentField(f) - BIG_ONE;
  const epsilonPattern = BigInt(f.bias - f.fractionBits) << exponentShift;
  return {
    smallestSubnormal: digitsOf(BIG_ONE),
    smallestNormal: digitsOf(BIG_ONE << exponentShift),
    largestFinite: digitsOf(
      (maxExponentPattern << exponentShift) |
        ((BIG_ONE << exponentShift) - BIG_ONE)
    ),
    epsilon: digitsOf(epsilonPattern),
  };
}

// ---------------------------------------------------------------------------
// Analysis
// ---------------------------------------------------------------------------

export interface Analysis {
  format: FloatFormat;
  fields: Fields;
  pattern: bigint;
  kind: Kind;
  bits: string;
  hex: string;
  nan: NanInfo | null;
  /** Unbiased exponent used for the value; null for zero, ∞ and NaN. */
  exponent: number | null;
  /** `1.0110…` for normal numbers, `0.0110…` for subnormal ones. */
  significand: string | null;
  exact: string | null;
  shortest: string | null;
  up: bigint | null;
  down: bigint | null;
  error: RoundingError | null;
  /** The entered decimal overflowed to infinity or underflowed to zero. */
  overflow: boolean;
  underflow: boolean;
}

export function analysePattern(pattern: bigint, f: FloatFormat): Analysis {
  const fields = fromPatternValue(pattern, f);
  const kind = classify(fields, f);
  const parts = exactParts(fields, f);
  let exponent: number | null = null;
  let significand: string | null = null;
  if (kind === "normal") {
    exponent = Number(fields.exponent) - f.bias;
    significand = `1.${fields.fraction.toString(2).padStart(f.fractionBits, "0")}`;
  } else if (kind === "subnormal") {
    exponent = f.minExponent;
    significand = `0.${fields.fraction.toString(2).padStart(f.fractionBits, "0")}`;
  }
  const sign = fields.sign === 1 ? "−" : "";
  return {
    format: f,
    fields,
    pattern,
    kind,
    bits: pattern.toString(2).padStart(f.total, "0"),
    hex: pattern
      .toString(16)
      .toUpperCase()
      .padStart(f.total / 4, "0"),
    nan: kind === "nan" ? nanInfo(fields, f) : null,
    exponent,
    significand,
    exact: parts ? `${sign}${plainDecimal(exactDigits(parts))}` : null,
    shortest: parts ? `${sign}${shortText(shortestDigits(fields, f))}` : null,
    up: neighbour(pattern, f, "up"),
    down: neighbour(pattern, f, "down"),
    error: null,
    overflow: false,
    underflow: false,
  };
}

export type FromDecimal =
  | { ok: true; analysis: Analysis }
  | { ok: false; error: "empty" | "invalid" | "tooLong" };

/** Convert decimal text to the nearest float of the given format. */
export function analyseDecimal(text: string, f: FloatFormat): FromDecimal {
  const parsed = parseDecimalText(text);
  if (!parsed.ok) {
    return parsed;
  }
  if (parsed.kind === "nan") {
    return {
      ok: true,
      analysis: analysePattern(toPatternValue(canonicalNan(f), f), f),
    };
  }
  if (parsed.kind === "infinity") {
    const analysis = analysePattern(
      toPatternValue(infinityFields(parsed.sign, f), f),
      f
    );
    return { ok: true, analysis: { ...analysis, overflow: parsed.overflowed } };
  }
  const { value } = parsed;
  const fields = encodeRational(value.sign, value.num, value.den, f);
  const analysis = analysePattern(toPatternValue(fields, f), f);
  const kind = classify(fields, f);
  return {
    ok: true,
    analysis: {
      ...analysis,
      error: roundingError(value, fields, f),
      overflow: kind === "infinity",
      underflow:
        parsed.underflowed || (kind === "zero" && value.num !== BIG_ZERO),
    },
  };
}

export type PatternParse =
  | { ok: true; pattern: bigint }
  | { ok: false; error: "empty" | "invalid" | "tooWide" };

const HEX_PREFIX = /^0[xX]/;
const BINARY_PREFIX = /^0[bB]/;
const HEX_DIGITS = /^[0-9a-fA-F]+$/;
const BINARY_DIGITS = /^[01]+$/;

/** Read a bit pattern as binary or hexadecimal; shorter input is padded on the left. */
export function parsePatternText(
  text: string,
  base: "binary" | "hex",
  f: FloatFormat
): PatternParse {
  const cleaned = text.replace(SEPARATORS, "");
  if (cleaned.length === 0) {
    return { ok: false, error: "empty" };
  }
  const isBinary = base === "binary";
  const digits = cleaned.replace(isBinary ? BINARY_PREFIX : HEX_PREFIX, "");
  if (!(isBinary ? BINARY_DIGITS : HEX_DIGITS).test(digits)) {
    return { ok: false, error: "invalid" };
  }
  const value = BigInt(`${isBinary ? "0b" : "0x"}${digits}`);
  if (bitLen(value) > f.total) {
    return { ok: false, error: "tooWide" };
  }
  return { ok: true, pattern: value };
}

// biome-ignore-end lint/suspicious/noBitwiseOperators: end of the IEEE 754 helpers
